import type React from "react";
import { memo } from "react";

import { useFocusTrap } from "react-use-focus-trap";

import { Heading } from "@react/components/Heading";
import { Icon } from "@react/components/Icon";
import { Image } from "@react/components/Image";
import { ModelDetails } from "@sections/ModalProvider/SizeGuideModal/ModelDetails.view";
import { SizeGuideTable } from "@sections/ModalProvider/SizeGuideModal/SizeGuideTable.view";
import { UnitToggle } from "@sections/ModalProvider/SizeGuideModal/UnitToggle.view";
import {
  type ModelMeasurementDetails,
  type SizeGuideImage,
  type SizeGuideDimensionsUnit,
  type StandardizedSizeGuideData,
} from "@tablescape/state";
import { tw, type TTailwindString } from "classnames";

const staticWidth = {
  modal: tw("max-w-[927px]" as TTailwindString),
  measurementImageMobile: tw(
    "min-w-[329px]" as TTailwindString,
    "max-w-[342px]" as TTailwindString
  ),
  measurementImageDesktop: tw(
    "md:min-w-[279px]" as TTailwindString,
    "md:max-w-[279px]" as TTailwindString
  ),
};

const styles = {
  modal: tw(
    "box-border",
    "w-screen",
    "h-dynamic-screen",
    "py-6",
    "md:w-auto",
    "landscape:h-auto" as TTailwindString,
    "landscape:max-h-dynamic-screen" as TTailwindString,
    "md:py-8",
    "md:rounded",
    "bg-char-50",
    "text-primary",
    "overflow-y-auto"
  ),
  innerWrapper: tw(
    "relative",
    "h-full",
    "p-0",
    "pl-6",
    "md:pl-8",
    "flex",
    "flex-col"
  ),
  closeButton: tw(
    "absolute",
    "-top-1",
    "right-3",
    "md:right-7",
    "p-0",
    "border-0",
    "bg-transparent",
    "cursor-pointer"
  ),
  content: tw(
    "flex",
    "flex-col",
    "flex-1",
    "md:flex-row",
    "md:flex-wrap",
    "md:flex-initial"
  ),
  tableWrapper: tw(
    "w-full",
    "pb-4",
    "pr-6",
    "mb-4",
    "overflow-y-auto",
    "md:w-auto",
    "md:flex-1",
    "md:pr-8",
    "md:overflow-y-visible"
  ),
  imageWrapper: tw(
    "flex-1",
    "mr-6",
    "mb-6",
    "md:mr-8",
    "md:mb-0",
    "md:flex-1",
    "flex",
    "flex-row",
    "flex-wrap",
    staticWidth.measurementImageMobile,
    staticWidth.measurementImageDesktop
  ),
  image: tw("w-full"),
  modelDetails: tw("w-full", "mb-6"),
};

type Props = {
  closeBtnAlt?: string;
  closeModal: (e: React.MouseEvent<HTMLElement> | KeyboardEvent) => void;
  image?: SizeGuideImage;
  modelDetails?: ModelMeasurementDetails;
  sizeGuideTableFields: string[];
  sizeGuideTableLabel: string;
  sizeGuideTableOptions: string[];
  sizeGuideTableValues: StandardizedSizeGuideData[][];
  title: string;
  toggleAlt?: string;
  toggleUnit: (unit: SizeGuideDimensionsUnit) => void;
  unit: SizeGuideDimensionsUnit;
};

const SizeGuideModalViewBase = ({
  closeBtnAlt,
  closeModal,
  image,
  modelDetails,
  sizeGuideTableFields,
  sizeGuideTableLabel,
  sizeGuideTableOptions,
  sizeGuideTableValues,
  title,
  toggleAlt,
  toggleUnit,
  unit,
}: Props) => {
  const [modalTrap] = useFocusTrap<HTMLElement>();

  return (
    <div
      aria-modal="true"
      className={styles.modal}
      data-theme="light"
      ref={modalTrap}
      role="dialog"
    >
      <div className={styles.innerWrapper}>
        <Heading as="h5">{title}</Heading>
        <button
          aria-label={closeBtnAlt}
          className={styles.closeButton}
          onClick={closeModal}
        >
          <Icon name="close" />
        </button>
        <UnitToggle
          onToggle={toggleUnit}
          toggleAlt={toggleAlt}
          unitSelected={unit}
        />
        <div className={styles.content}>
          <div className={styles.tableWrapper}>
            <SizeGuideTable
              fields={sizeGuideTableFields}
              optionLabels={sizeGuideTableOptions}
              tableLabel={sizeGuideTableLabel}
              values={sizeGuideTableValues}
            />
          </div>
          {image && (
            <div className={styles.imageWrapper}>
              <div className={styles.image}>
                <Image className={tw("h-auto")} data={image} />
              </div>
              {modelDetails && (
                <div className={styles.modelDetails}>
                  <ModelDetails {...modelDetails} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SizeGuideModalView = memo(SizeGuideModalViewBase);
